/**
 * Lógica de transiciones de estado de órdenes.
 * Espejo del backend TRANSICIONES_ORDEN (backend/apps/ordenes/models.py).
 */

import type { EstadoOrden } from "@/hooks/use-ordenes";

/** Mapa de transiciones válidas desde cada estado */
const TRANSICIONES_ORDEN: Record<EstadoOrden, EstadoOrden[]> = {
  EN_EXTRACCION: ["PENDIENTE", "BAJA"],
  PENDIENTE: ["CON_RESULTADO", "BAJA"],
  CON_RESULTADO: ["FINALIZADA", "PENDIENTE"],
  FINALIZADA: [],
  BAJA: [],
};

/** Helper para acceder al mapa de forma segura (evita prototype pollution) */
function getTransitions(estado: EstadoOrden): EstadoOrden[] {
  return Object.hasOwn(TRANSICIONES_ORDEN, estado) ? TRANSICIONES_ORDEN[estado] : [];
}

/** Retorna transiciones válidas desde un estado (excluyendo BAJA, que tiene su propio flujo) */
export function getValidTransitions(estado: EstadoOrden): EstadoOrden[] {
  return getTransitions(estado).filter((e) => e !== "BAJA");
}

/** Retorna true si el estado es terminal (sin transiciones posibles) */
export function isTerminalState(estado: EstadoOrden): boolean {
  return estado === "FINALIZADA" || estado === "BAJA";
}

/** Retorna true si la orden puede ser dada de baja desde su estado actual */
export function canDarDeBaja(estado: EstadoOrden): boolean {
  return getTransitions(estado).includes("BAJA");
}

/** Metadata de UI para cada transición */
export interface TransitionMeta {
  targetState: EstadoOrden;
  labelKey: string;
  icon: "Clock" | "FlaskConical" | "CheckCircle2";
  variant: "default" | "outline";
}

/** Retorna metadata de UI para un estado destino */
export function getTransitionMeta(targetState: EstadoOrden): TransitionMeta | undefined {
  const meta: Partial<Record<EstadoOrden, TransitionMeta>> = {
    PENDIENTE: {
      targetState: "PENDIENTE",
      labelKey: "ordenes.transitions.toPendiente",
      icon: "Clock",
      variant: "outline",
    },
    CON_RESULTADO: {
      targetState: "CON_RESULTADO",
      labelKey: "ordenes.transitions.toConResultado",
      icon: "FlaskConical",
      variant: "outline",
    },
    FINALIZADA: {
      targetState: "FINALIZADA",
      labelKey: "ordenes.transitions.toFinalizada",
      icon: "CheckCircle2",
      variant: "default",
    },
  };
  return meta[targetState];
}
